/**
 * WorkOrderPoEmailDialog
 * ----------------------
 * The work-order twin of SupplierPoEmailDialog. Drafts either the contractor
 * work-order PO or the supplier parts PO in Outlook (mailto fallback).
 *
 * GET  /api/admin/work-orders/{id}/vendor-po-email?kind=contractor|supplier
 * POST /api/admin/work-orders/{id}/vendor-po-email/build
 * POST /api/admin/work-orders/{id}/vendor-po-email/outlook-draft
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, ExternalLink, FileText, Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const blankLine = () => ({ code: "", description: "", location: "", quantity: "", unit_price: "" });

export const WorkOrderPoEmailDialog = ({ open, onOpenChange, workOrderId, kind = "contractor", token, onLogged }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isContractor = kind === "contractor";
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !workOrderId) return;
    setLoading(true);
    setPreview(null);
    axios.get(`${API}/api/admin/work-orders/${workOrderId}/vendor-po-email`,
      { headers, params: { kind } })
      .then(({ data }) => { setCtx(data); setIdx(0); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load the PO context"))
      .finally(() => setLoading(false));
  }, [open, workOrderId, kind, headers]);

  useEffect(() => {
    if (!ctx) return;
    const v = (ctx.vendors || [])[idx] || {};
    setForm({
      kind: ctx.kind || kind,
      vendor_name: v.vendor_name || "",
      vendor_email: v.vendor_email || "",
      vendor_po_id: v.vendor_po_id || null,
      vendor_po_number: v.vendor_po_number || "",
      greeting_name: v.greeting_name || "",
      to: v.vendor_email || "",
      cc: "",
      hospital_po_number: ctx.hospital_po_number || "",
      site: ctx.site || "",
      site_contact: ctx.site_contact || "",
      site_phone: ctx.site_phone || "",
      scheduled_for: ctx.scheduled_for || "",
      nte_amount: ctx.nte_amount ?? "",
      sign_off_name: ctx.sign_off_name || "",
      extra_note: "",
      items: (v.items && v.items.length ? v.items : ctx.all_items || []).map((l) => ({
        code: l.code || "", description: l.description || "", location: l.location || "",
        quantity: l.quantity ?? "", unit_price: l.unit_price ?? "",
      })),
      options: { ...(ctx.defaults || {}) },
    });
    setPreview(null);
  }, [ctx, idx, kind]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOpt = (k, v) => setForm((p) => ({ ...p, options: { ...p.options, [k]: v } }));
  const setLine = (i, k, v) => setForm((p) => ({
    ...p, items: p.items.map((l, j) => (j === i ? { ...l, [k]: v } : l)),
  }));

  const payload = () => ({
    ...form,
    to: form.to.split(",").map((s) => s.trim()).filter(Boolean),
    cc: form.cc.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const post = async (suffix) => axios.post(
    `${API}/api/admin/work-orders/${workOrderId}/vendor-po-email/${suffix}`, payload(), { headers });

  const buildPreview = async () => {
    if (!form?.vendor_name?.trim()) return toast.error(`Pick or name a ${kind} first`);
    setBusy(true);
    try { setPreview((await post("build")).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not build the email"); }
    finally { setBusy(false); }
  };

  const openInOutlook = async () => {
    if (!form?.vendor_name?.trim()) return toast.error(`Pick or name a ${kind} first`);
    setBusy(true);
    try {
      const { data } = await post("outlook-draft");
      window.open(data.web_link, "_blank", "noopener");
      toast.success("Draft waiting in Outlook", {
        description: "Attach the PO and scope documents, then send.",
      });
      onLogged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Outlook drafting is unavailable", {
        description: "Use “Open in mail app” to compose locally instead.",
      });
      if (!preview) buildPreview();
    } finally { setBusy(false); }
  };

  const openMailto = async () => {
    let p = preview;
    if (!p) {
      try { p = (await post("build")).data; setPreview(p); }
      catch { return toast.error("Could not build the email"); }
    }
    window.location.href = p.mailto_url;
  };

  const vendors = ctx?.vendors || [];
  const optionRows = ctx?.option_rows || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white"
        data-testid={`wo-po-email-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-700" />
            {isContractor ? "Send work order PO to contractor" : "Send parts PO to supplier"}
          </DialogTitle>
          <DialogDescription>
            {isContractor
              ? "Builds the work-order PO email for the awarded contractor — scope, site, schedule and NTE — and opens it in Outlook so you can attach the PO."
              : "Builds the parts PO email for the supplier so the material ships to the job site before the crew mobilizes."}
          </DialogDescription>
        </DialogHeader>

        {loading || !form ? (
          <div className="py-16 text-center text-slate-500" data-testid="wo-po-email-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {vendors.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="wo-po-email-vendor-tabs">
                {vendors.map((v, i) => (
                  <button key={v.award_id || i} type="button" onClick={() => setIdx(i)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                      i === idx ? "bg-indigo-700 text-white border-indigo-700"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                    data-testid={`wo-po-email-vendor-tab-${i}`}>
                    {v.vendor_name}
                  </button>
                ))}
              </div>
            )}
            {vendors.length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5"
                data-testid="wo-po-email-no-vendor">
                {isContractor
                  ? "No contractor has been awarded yet — type the company name and email below to send the PO anyway."
                  : "No supplier is assigned to this work order — type the supplier name and email below."}
              </p>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={`${isContractor ? "Contractor" : "Supplier"} name *`}>
                <Input value={form.vendor_name} onChange={(e) => set("vendor_name", e.target.value)}
                  data-testid="wo-po-email-vendor-name" />
              </Field>
              <Field label="Greeting name (first name)">
                <Input value={form.greeting_name} onChange={(e) => set("greeting_name", e.target.value)}
                  data-testid="wo-po-email-greeting" />
              </Field>
              <Field label="To (comma separated)">
                <Input value={form.to} onChange={(e) => set("to", e.target.value)}
                  data-testid="wo-po-email-to" />
              </Field>
              <Field label="CC (optional)">
                <Input value={form.cc} onChange={(e) => set("cc", e.target.value)}
                  data-testid="wo-po-email-cc" />
              </Field>
              <Field label="Our PO # (subject line)">
                <Input value={form.vendor_po_number}
                  onChange={(e) => set("vendor_po_number", e.target.value)}
                  placeholder="SM-WO-10042" data-testid="wo-po-email-vendor-po" />
              </Field>
              <Field label="Hospital PO #">
                <Input value={form.hospital_po_number}
                  onChange={(e) => set("hospital_po_number", e.target.value)}
                  data-testid="wo-po-email-hospital-po" />
              </Field>
              <Field label="Site / location">
                <Input value={form.site} onChange={(e) => set("site", e.target.value)}
                  data-testid="wo-po-email-site" />
              </Field>
              <Field label="Site contact">
                <Input value={form.site_contact} onChange={(e) => set("site_contact", e.target.value)}
                  data-testid="wo-po-email-site-contact" />
              </Field>
              <Field label="Scheduled for">
                <Input value={form.scheduled_for} onChange={(e) => set("scheduled_for", e.target.value)}
                  placeholder="Tue, Oct 21 · 7:00 AM" data-testid="wo-po-email-scheduled" />
              </Field>
              {isContractor && (
                <Field label="Not-to-exceed amount">
                  <Input value={form.nte_amount} onChange={(e) => set("nte_amount", e.target.value)}
                    placeholder="2400" data-testid="wo-po-email-nte" />
                </Field>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {isContractor ? "Scope of work on this PO" : "Parts on this PO"}
                </p>
                <Button type="button" variant="outline" size="sm"
                  onClick={() => setForm((p) => ({ ...p, items: [...p.items, blankLine()] }))}
                  data-testid="wo-po-email-add-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-[1.1fr_2fr_1.1fr_0.7fr_0.9fr_28px] gap-1 bg-slate-100
                  px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <span>{isContractor ? "Scope item" : "Part #"}</span>
                  <span>Description</span>
                  <span>{isContractor ? "Location" : "Need by"}</span>
                  <span>Qty</span>
                  <span>{isContractor ? "Amount" : "Unit price"}</span>
                  <span />
                </div>
                {form.items.map((l, i) => (
                  <div key={i} className="grid grid-cols-[1.1fr_2fr_1.1fr_0.7fr_0.9fr_28px] gap-1
                    px-2 py-1.5 border-t border-slate-100" data-testid={`wo-po-email-line-${i}`}>
                    <Input className="h-8 text-xs" value={l.code}
                      onChange={(e) => setLine(i, "code", e.target.value)}
                      data-testid={`wo-po-email-line-code-${i}`} />
                    <Input className="h-8 text-xs" value={l.description}
                      onChange={(e) => setLine(i, "description", e.target.value)}
                      data-testid={`wo-po-email-line-desc-${i}`} />
                    <Input className="h-8 text-xs" value={l.location}
                      onChange={(e) => setLine(i, "location", e.target.value)} />
                    <Input className="h-8 text-xs" value={l.quantity}
                      onChange={(e) => setLine(i, "quantity", e.target.value)}
                      data-testid={`wo-po-email-line-qty-${i}`} />
                    <Input className="h-8 text-xs" value={l.unit_price}
                      onChange={(e) => setLine(i, "unit_price", e.target.value)}
                      data-testid={`wo-po-email-line-price-${i}`} />
                    <button type="button" className="text-slate-400 hover:text-rose-600"
                      onClick={() => setForm((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                      data-testid={`wo-po-email-remove-line-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {isContractor ? "Instructions in the email" : "Shipping instructions in the email"}
              </p>
              {optionRows.map((o) => (
                <label key={o.k} className="flex items-start gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={!!form.options[o.k]}
                    onChange={(e) => setOpt(o.k, e.target.checked)}
                    data-testid={`wo-po-email-opt-${o.k}`} className="mt-0.5" />
                  {o.label}
                </label>
              ))}
              {!isContractor && (
                <div className="grid sm:grid-cols-2 gap-3 pt-1">
                  <Field label="Hospital FedEx account # (blank = omit)">
                    <Input value={form.options.freight_account || ""}
                      onChange={(e) => setOpt("freight_account", e.target.value)}
                      data-testid="wo-po-email-fedex-account" />
                  </Field>
                  <Field label="Heavy-freight phone">
                    <Input value={form.options.freight_phone || ""}
                      onChange={(e) => setOpt("freight_phone", e.target.value)}
                      data-testid="wo-po-email-fedex-phone" />
                  </Field>
                </div>
              )}
              <Field label="Anything else to add (optional)">
                <Textarea rows={2} value={form.extra_note}
                  onChange={(e) => set("extra_note", e.target.value)}
                  data-testid="wo-po-email-extra-note" />
              </Field>
              <Field label="Sign off as">
                <Input value={form.sign_off_name} onChange={(e) => set("sign_off_name", e.target.value)}
                  data-testid="wo-po-email-signoff" />
              </Field>
            </div>

            {preview && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3"
                data-testid="wo-po-email-preview">
                <p className="text-[11px] font-bold text-indigo-900 mb-2">
                  Subject: <span className="font-normal">{preview.subject}</span>
                </p>
                <div className="bg-white rounded border border-slate-200 p-3 max-h-72 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: preview.body_html }} />
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" onClick={openInOutlook} disabled={busy}
                className="bg-indigo-700 hover:bg-indigo-800" data-testid="wo-po-email-outlook-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <ExternalLink className="w-4 h-4 mr-1.5" />}
                Open draft in Outlook
              </Button>
              <Button type="button" variant="outline" onClick={openMailto} disabled={busy}
                data-testid="wo-po-email-mailto-btn">
                <Mail className="w-4 h-4 mr-1.5" /> Open in mail app
              </Button>
              <Button type="button" variant="ghost" onClick={buildPreview} disabled={busy}
                data-testid="wo-po-email-preview-btn">
                Preview email
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
      {label}
    </label>
    {children}
  </div>
);

export default WorkOrderPoEmailDialog;
